import { useState, type ReactNode } from 'react';
import { ChevronDown, IdCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

import { VIEWS } from '@/config/views';
import { useAuth } from '@/features/auth/hooks/useAuth';
import {
  containsActive,
  isNavPathActive,
  MenuIcon,
  menuLeaves,
  useMenu,
  type MenuNode,
} from '@/features/navigation';
import { ErrorState, LoadingState } from '@/shared/data';
import { cn } from '@/shared/lib/cn';

import { BrandMark } from './BrandMark';

const linkClass = (active: boolean) =>
  cn(
    'flex items-center gap-2.5 rounded-(--radius-control) px-3 py-2 text-sm transition-colors',
    active
      ? 'bg-brand-900 font-medium text-white'
      : 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
  );

function NavLeaf({
  to,
  label,
  icon,
  allPaths,
}: {
  to: string;
  label: string;
  icon: ReactNode;
  allPaths: readonly string[];
}) {
  const { pathname } = useLocation();
  const active = isNavPathActive(pathname, to, allPaths);
  return (
    <Link to={to} aria-current={active ? 'page' : undefined} className={linkClass(active)}>
      {icon}
      {label}
    </Link>
  );
}

function NavGroup({ node, allPaths }: { node: MenuNode; allPaths: readonly string[] }) {
  const { pathname } = useLocation();
  const hasActive = containsActive(node, pathname, allPaths);
  // `null` until the user toggles: the group follows the URL by default, so
  // landing on /trabajadores opens «Personal» without remembering anything.
  const [toggled, setToggled] = useState<boolean | null>(null);
  const open = toggled ?? hasActive;

  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setToggled(!open)}
        className={cn(
          'flex w-full items-center gap-2.5 rounded-(--radius-control) px-3 py-2 text-sm transition-colors',
          hasActive
            ? 'text-ink font-medium'
            : 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
        )}
      >
        <MenuIcon name={node.icon} className="size-4" />
        <span className="flex-1 text-left">{node.label}</span>
        <ChevronDown
          className={cn('size-4 transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </button>
      {open && (
        <div className="border-border ml-5 flex flex-col gap-0.5 border-l pl-2">
          <NavNodes nodes={node.children} allPaths={allPaths} />
        </div>
      )}
    </div>
  );
}

function NavNodes({
  nodes,
  allPaths,
}: {
  nodes: readonly MenuNode[];
  allPaths: readonly string[];
}) {
  return nodes.map((node) =>
    node.path === null ? (
      <NavGroup key={node.code} node={node} allPaths={allPaths} />
    ) : (
      <NavLeaf
        key={node.code}
        to={node.path}
        label={node.label}
        icon={<MenuIcon name={node.icon} className="size-4" />}
        allPaths={allPaths}
      />
    ),
  );
}

export function Sidebar() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const menu = useMenu();
  const nodes = menu.data ?? [];
  const showGenericHome = !user?.scope.is_global && !user?.role_codes.includes('EXECUTIVE');
  const allPaths = [VIEWS.home.path, ...menuLeaves(nodes).map((leaf) => leaf.path)];

  return (
    <aside className="bg-surface border-border hidden w-60 shrink-0 border-r p-3 md:block">
      <BrandMark className="mb-6" />
      <nav className="flex flex-col gap-0.5">
        {/* Inicio lives in code, not in the menu table: whether it shows
            depends on the role-home redirect, and it keeps the platform
            navigable when the menu is empty or fails to load. */}
        {showGenericHome && (
          <NavLeaf
            to={VIEWS.home.path}
            label={t('nav.home')}
            icon={<IdCard className="size-4" aria-hidden />}
            allPaths={allPaths}
          />
        )}
        {menu.isLoading ? (
          <LoadingState rows={6} />
        ) : menu.isError ? (
          <ErrorState error={menu.error} onRetry={() => void menu.refetch()} />
        ) : (
          <NavNodes nodes={nodes} allPaths={allPaths} />
        )}
      </nav>
    </aside>
  );
}
